import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader, Modal } from 'ncoded-component-library';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import confirm from 'modules/confirm';
import SelectField from 'components/SelectField';
import TransWithNda from 'components/TransWithNda';
import validations from 'validations';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  DATA_ROOM_NDA_SIGNING_STATUS,
  DataRoomNdaSigner,
  DataRoomNdaSigningStatus,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import DataRoomNdaContext from 'router/subrouters/DataRoom/providers/DataRoomNda/DataRoomNda.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SIZE } from 'models/Size';
import { getArrayLength } from 'utils';
import { NDA_COPIES } from '../DataRoomNdaStatusBadge/data';
import { getFirstSignerName, getSignerIds } from '../../utils';
import ResolveMultiSelectField from 'components/ResolveMultiSelectField';

import './DataRoomNdaOverrideStatusModal.styles.scss';

const INITIAL_VALUES: Partial<OverrideStatusFormValues> = {
  dataRoomNdaIds: [],
};

type OverrideStatusFormValues = {
  dataRoomNdaIds: string[];
  status: DataRoomNdaSigningStatus;
};

type DataRoomNdaOverrideStatusModalProps = {
  className?: string;
  dataRoomId: string;
  signers: DataRoomNdaSigner[];
  onUpdated: () => void;
};

const DataRoomNdaOverrideStatusModal: React.ForwardRefRenderFunction<
  OverlayRef,
  DataRoomNdaOverrideStatusModalProps
> = (props, ref) => {
  const { className, dataRoomId, signers, onUpdated } = props;

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { dataRoomNdas } = useContext(DataRoomNdaContext);
  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const classes = classNames('data-room-nda-override-status-modal', className);

  const signersCount = getArrayLength(signers);
  const firstSignerName = getFirstSignerName(signers);

  const ndaOptions = useMemo(
    () => dataRoomNdas.map(({ _id, name }) => ({ value: _id, label: name })),
    [dataRoomNdas],
  );

  const statusOptions = useMemo(
    () =>
      Object.values(DATA_ROOM_NDA_SIGNING_STATUS)
        .filter((value) => value !== DATA_ROOM_NDA_SIGNING_STATUS.NOT_SENT)
        .map((value) => ({
          value,
          label: <TransWithNda i18nKey={NDA_COPIES[value]} />,
        })),
    [],
  );

  const onSubmit = useCallback(
    async ({ dataRoomNdaIds, status }: OverrideStatusFormValues) => {
      if (
        status !== DATA_ROOM_NDA_SIGNING_STATUS.NDA_EXECUTED ||
        (await confirm({
          title: t('ndaChangeToSigned'),
          content: <TransWithNda i18nKey="dataRoomNdaChangeToSignedMessage" />,
          cancelBtnText: t('goBack'),
          submitBtnText: t('submit'),
        }))
      ) {
        try {
          await dataRoomApi.nda.overrideNdaStatus(
            dataRoomId,
            getSignerIds(signers),
            dataRoomNdaIds,
            status,
          );

          popSuccess(
            t('bulkOverrideNdaStatusSuccess', {
              count: signersCount,
              name: firstSignerName,
            }),
          );
          modal?.close();
          onUpdated();
        } catch (e) {
          popServerError(e);
        }
      }
    },
    [
      dataRoomId,
      firstSignerName,
      modal,
      signers,
      signersCount,
      t,
      onUpdated,
      popServerError,
      popSuccess,
    ],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('bulkOverrideNdaStatusTitle', {
        count: signersCount,
        name: firstSignerName,
      })}
    >
      <TransWithNda i18nKey="reminderThatUpdatingNDA" />

      <Form
        initialValues={INITIAL_VALUES}
        onSubmit={onSubmit}
        render={({ invalid, submitting, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Field
              label={t('nda')}
              name="dataRoomNdaIds"
              component={ResolveMultiSelectField}
              options={ndaOptions}
              validate={validations.minLength(t('requiredField'), 1)}
            />

            <Field
              label={t('status')}
              name="status"
              component={SelectField}
              options={statusOptions}
              validate={validations.required()}
            />

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={invalid || submitting}>
                {t('confirmStatus')}
                {submitting && (
                  <Loader size={SIZE.SMALL} inline overlay={false} />
                )}
              </Button>
            </div>
          </form>
        )}
      />
    </Modal>
  );
};

export default forwardRef(DataRoomNdaOverrideStatusModal);
